import { LabelType, Options } from '@angular-slider/ngx-slider'
import { CurrencyPipe } from '@angular/common'
import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ViewChild } from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { addDays, format } from 'date-fns'
import { combineLatest } from 'rxjs'
import { environment } from '../../../../../environments/environment'
import { AssetType, Criteria, ScreenSize } from '../../../../../graphql'
import { CurrencyService } from '../../../../injectable/currency-service'
import { searchCriteriaFromStorage, UnsubscribeOnDestroy } from '../../../../utility'
import { defaultMoreFilters, MoreFilters, toCurrency } from '../more-filters'
import { SearchFiltersModalComponent } from '../search-filters-modal/search-filters-modal'
import { SearchFiltersExtendedComponent } from '../search-filters-extended/search-filters-extended'
import { dialogWidth } from '../../../../constant'

function constructFormGroup() {
    const criteria = searchCriteriaFromStorage()
    return new FormGroup({
        bedrooms: new FormControl(criteria?.roomsAndBedsCriterion?.bedrooms ?? 0),
        bathrooms: new FormControl(criteria?.roomsAndBedsCriterion?.bathrooms ?? 0),
        includeHotels: new FormControl(criteria?.assetTypesCriterion?.assetTypes?.find(at => at === AssetType.Hotel)),
        includeProperties: new FormControl(criteria?.assetTypesCriterion?.assetTypes?.find(at => at === AssetType.Property))
    })
}

function moreFiltersFromStorage() {
    const criteria = searchCriteriaFromStorage()
    const defaults = defaultMoreFilters()
    return {
        brandsCriterion: criteria?.brandsCriterion ?? defaults.brandsCriterion,
        amenitiesCriterion: criteria?.amenitiesCriterion ?? defaults.amenitiesCriterion,
        priceRangeCriterion: criteria?.priceRangeCriterion ?? defaults.priceRangeCriterion,
        roomsAndBedsCriterion: criteria?.roomsAndBedsCriterion ?? defaults.roomsAndBedsCriterion,
        minimumStayNightsCriterion: criteria?.minimumStayNightsCriterion ?? defaults.minimumStayNightsCriterion,
        styleCriterion: criteria?.styleCriterion ?? defaults.styleCriterion
    }
}

const BEDROOMS = 'bedrooms'
const BATHROOMS = 'bathrooms'
const MIN_PRICE_VALUE = 0
const MAX_PRICE_VALUE = 100000

@Component({
    selector: 'app-search-filters',
    templateUrl: 'search-filters.html',
    styleUrls: ['search-filters.scss'],
    providers: [CurrencyPipe]
})
export class SearchFiltersComponent extends UnsubscribeOnDestroy implements OnChanges {

    @Input() minimumConsecutiveNights: number
    @Input() listingsOnMapCount: number
    @Input() layout = 'row'
    @Input() disabled = false

    private minimumDaysFuture = 7
    private rangeFilterCurrency = environment.tenant.currency
    minValue = MIN_PRICE_VALUE
    maxValue = MAX_PRICE_VALUE
    options: Options = this.constructOptions()
    property = AssetType.Property
    hotel = AssetType.Hotel
    formGroup: FormGroup = constructFormGroup()
    moreFilters: MoreFilters = moreFiltersFromStorage()
    selectedFilterCount = 0
    selectedExtendedFilterCount = 0
    moreFiltersDialogRef: MatDialogRef<SearchFiltersModalComponent>
    includeComingSoon: boolean
    protected readonly AssetType = AssetType

    @Output() criteria = new EventEmitter<Criteria>()
    @Output() $moreFiltersChange = new EventEmitter<MoreFilters>()
    @Output() $selectedFilterCountChanged = new EventEmitter<number>()

    @ViewChild(SearchFiltersExtendedComponent) searchFiltersExtended!: SearchFiltersExtendedComponent

    constructor(
        private _dialog: MatDialog,
        private _currencyService: CurrencyService,
        private _currencyPipe: CurrencyPipe,
    ) {
        super()
        this._subscriptions.push(this.formGroup.valueChanges.subscribe(_ => this.search()))
        this.setupPresentmentCurrency()
        this.setSelectedFilterCountFromStorage()
    }

    private constructOptions(ceil: number = 100000) {
        return {
            floor: 0,
            ceil,
            translate: (value: number, label: LabelType): string => {
                switch (label) {

                    default:
                        return this._currencyPipe.transform(value, this._currencyService.getPresentmentCurrency(), 'symbol-narrow', '1.0-0')
                }
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.listingsOnMapCount?.currentValue !== changes?.listingsOnMapCount?.previousValue && this.moreFiltersDialogRef?.componentInstance) {
            this.moreFiltersDialogRef.componentInstance.updateListingCount(this.listingsOnMapCount)
        }
    }

    private setupPresentmentCurrency() {
        const criteria = searchCriteriaFromStorage()
        this._currencyService.presentmentCurrency$
            .subscribe(currency => {
            const ceil$ = this._currencyService.convert(currency, {
                amountMicros: this.options.ceil,
                currency: this.rangeFilterCurrency
            })

            const minValue$ = this._currencyService.convert( currency, {
                amountMicros: (criteria?.priceRangeCriterion?.minimum?.amountMicros ?? (this.minValue * 1000000)) / 1000000,
                currency: criteria?.priceRangeCriterion?.minimum?.currency ?? this.rangeFilterCurrency
            })

            const maxValue$ = this._currencyService.convert( currency, {
                amountMicros: (criteria?.priceRangeCriterion?.maximum?.amountMicros ?? (this.maxValue * 1000000)) / 1000000,
                currency: criteria?.priceRangeCriterion?.maximum?.currency ?? this.rangeFilterCurrency
            })

            combineLatest([ceil$, minValue$, maxValue$])
                .subscribe(([ceil, minvalue, maxValue]) => {
                    this.options = this.constructOptions(ceil.amountMicros)
                    this.maxValue = maxValue.amountMicros
                    this.minValue = minvalue.amountMicros
                    this.rangeFilterCurrency = currency
                })
        })
    }

    setSelectedFilterCountFromStorage() {
        const brandFilterCount = this.moreFilters?.brandsCriterion?.brandIds.length || 0
        const amenitiesFilterCount = this.moreFilters?.amenitiesCriterion?.selectedAmenityIds.length || 0
        const propertyStyleCount = this.moreFilters?.styleCriterion?.propertyStyles.length || 0
        let shortStayFilterCount =
            this.moreFilters?.minimumStayNightsCriterion?.minimumStayNights ?? 0
        if (shortStayFilterCount > 0) {
            shortStayFilterCount = 1
        }
        this.selectedExtendedFilterCount = brandFilterCount + amenitiesFilterCount + shortStayFilterCount + propertyStyleCount
    }

    search(paging = { offset: 0, size: 50 }) {
        if (!this.formGroup.valid) { return }

        const assetTypes = []
        if (this.formGroup.value.includeProperties) {
            assetTypes.push(AssetType.Property)
        }
        if (this.formGroup.value.includeHotels) {
            assetTypes.push(AssetType.Hotel)
        }
        const criteria: Criteria = {
            priceRangeCriterion: {
                minimum: toCurrency(Math.floor(this.minValue), this.rangeFilterCurrency),
                maximum: toCurrency(Math.floor(this.maxValue), this.rangeFilterCurrency)
            },
            roomsAndBedsCriterion: {
                bedrooms: this.formGroup.value.bedrooms,
                bathrooms: this.formGroup.value.bathrooms,
                beds: this?.moreFilters?.roomsAndBedsCriterion?.beds ?? 0
            },
            metadata: {
                screenSize: ScreenSize.Large,
                includeComingSoon: this.includeComingSoon
            },
            paging,
            brandsCriterion: this?.moreFilters?.brandsCriterion ?? {
                brandIds: []
            },
            amenitiesCriterion: this?.moreFilters?.amenitiesCriterion ?? {
                selectedAmenityIds: []
            },
            assetTypesCriterion: {
                assetTypes
            },
            minimumStayNightsCriterion: this?.moreFilters?.minimumStayNightsCriterion ?? {
                minimumStayNights: 0
            },
            styleCriterion: this?.moreFilters?.styleCriterion ?? {
                propertyStyles: []
            }
        }

        if (this.formGroup.value.checkin && this.formGroup.value.checkout) {
            criteria.datesCriterion = {
                checkin: format(this.formGroup.value.checkin, 'yyyy-MM-dd'),
                checkout: format(this.formGroup.value.checkout, 'yyyy-MM-dd')
            }
        } else {
            criteria.availableFromCriterion = {
                minimumConsecutiveNights: this.minimumConsecutiveNights,
                start: addDays(new Date(), this.minimumDaysFuture)
            }
        }
        this.onSelectedFilterCountChanged(this.selectedExtendedFilterCount)
        this.criteria.next(criteria)
    }

    openFiltersModal() {
        this.moreFiltersDialogRef = this._dialog.open(SearchFiltersModalComponent, { 
            width: dialogWidth.sm, 
            data: {
            moreFilters: this.moreFilters, 
            listingCount: this.listingsOnMapCount }
        })

        this.moreFiltersDialogRef.componentInstance.$moreFiltersChange.subscribe($event => this.onMoreFiltersChanged($event))
        this.moreFiltersDialogRef.componentInstance.$selectedFilterCountChanged.subscribe($event => this.onSelectedFilterCountChanged($event, true))
    }

    clearFilters(fieldName: string) {
      this.formGroup.get(fieldName).reset(0)
    }

    clearAllFilters() {
        this.formGroup.reset()
        this.selectedExtendedFilterCount = 0
        this.moreFilters = defaultMoreFilters()
        this.clearFilters(BEDROOMS)
        this.clearFilters(BATHROOMS)
        this.resetPriceRange()
    }

    clearAllExtendedFilters() {
        this.searchFiltersExtended.clearAllFilters()
    }

    onSelectedFilterCountChanged(extendedFilterCount: number, extendedFiltersOnly?: boolean) {
        this.selectedExtendedFilterCount = extendedFilterCount

        if (extendedFiltersOnly) {
            this.selectedExtendedFilterCount = extendedFilterCount
            this.$selectedFilterCountChanged.emit(extendedFilterCount)
        } 
        else {    
            this.selectedFilterCount = this.getSelectedFilterCount()
            this.$selectedFilterCountChanged.emit(this.selectedFilterCount + extendedFilterCount)
        }
    }

    getSelectedFilterCount() {
        let count = 0
        if (this.formGroup.value.includeProperties) {
           count ++
        }
        if (this.formGroup.value.includeHotels) {
            count ++
        }
        if (this.formGroup.value.bedrooms > 0) {
            count ++
        }
        if (this.formGroup.value.bathrooms > 0) {
            count ++
        }
        if (this.minValue !== MIN_PRICE_VALUE || this.maxValue !== MAX_PRICE_VALUE) {
            count ++
        }
        return count
    }

    onMoreFiltersChanged($event: MoreFilters) {
        this.moreFilters = $event
        this.search()
    }

    resetPriceRange() {
        this.minValue = 0
        this.maxValue = Math.ceil(this.options.ceil)
        this.search()
    }
}
